import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { CSSBiomePoster } from './CSSBiomePoster';

function isWebGLAvailable(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.location.search.includes('nowebgl') || window.location.hash.includes('nowebgl')) {
    return false;
  }
  try {
    const canvas = document.createElement('canvas');
    return !!(
      window.WebGLRenderingContext &&
      (canvas.getContext('webgl2') ||
        canvas.getContext('webgl') ||
        canvas.getContext('experimental-webgl'))
    );
  } catch {
    return false;
  }
}

export const LivingBiomeCanvas3D: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [webglReady, setWebglReady] = useState<boolean>(() => isWebGLAvailable());

  useEffect(() => {
    if (!isWebGLAvailable()) {
      setWebglReady(false);
      return;
    }

    const container = containerRef.current;
    if (!container) return;

    let renderer: THREE.WebGLRenderer | null = null;
    let canvas: HTMLCanvasElement | null = null;
    let scene: THREE.Scene | null = null;
    let camera: THREE.PerspectiveCamera | null = null;
    let geometry: THREE.PlaneGeometry | null = null;
    let mesh: THREE.Mesh | null = null;
    let wireframeMesh: THREE.LineSegments | null = null;
    let animId = 0;
    let onContextLost: ((e: Event) => void) | null = null;
    let onContextRestored: (() => void) | null = null;

    try {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const isMobile = width < 768;

      scene = new THREE.Scene();
      scene.fog = new THREE.FogExp2(0xF9FAF7, 0.038);

      camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 100);
      camera.position.set(0, -9, 7.5);
      camera.lookAt(0, 0, 1.2);

      renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: true,
        powerPreference: 'high-performance',
        precision: 'mediump',
        preserveDrawingBuffer: true,
      });
      renderer.setClearColor(0xF9FAF7, 1.0);
      renderer.setSize(width, height);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.35));

      canvas = renderer.domElement;
      canvas.style.display = 'block';
      canvas.style.position = 'absolute';
      canvas.style.top = '0';
      canvas.style.left = '0';
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      canvas.style.backgroundColor = '#F9FAF7';
      canvas.style.pointerEvents = 'none';

      container.appendChild(canvas);

      onContextLost = (e: Event) => {
        e.preventDefault();
        setWebglReady(false);
      };
      onContextRestored = () => {
        setWebglReady(true);
      };
      canvas.addEventListener('webglcontextlost', onContextLost, false);
      canvas.addEventListener('webglcontextrestored', onContextRestored, false);

      // Procedural Living Terrain Grid
      const segmentsX = isMobile ? 48 : 72;
      const segmentsY = isMobile ? 48 : 72;
      const planeWidth = isMobile ? 24 : 32;
      const planeHeight = isMobile ? 24 : 32;

      geometry = new THREE.PlaneGeometry(planeWidth, planeHeight, segmentsX, segmentsY);
      const count = geometry.attributes.position.count;
      const colors = new Float32Array(count * 3);

      const colorValley = new THREE.Color(0xF0F4ED);
      const colorMid = new THREE.Color(0xC2D7C7);
      const colorRidge = new THREE.Color(0x059669);

      for (let i = 0; i < count; i++) {
        const i3 = i * 3;
        colors[i3] = colorValley.r;
        colors[i3 + 1] = colorValley.g;
        colors[i3 + 2] = colorValley.b;
      }
      geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

      // Shaded Surface Material (Soft alabaster with vertex coloration)
      const material = new THREE.MeshBasicMaterial({
        vertexColors: true,
        transparent: true,
        opacity: 0.85,
        wireframe: false,
      });

      mesh = new THREE.Mesh(geometry, material);
      scene.add(mesh);

      // Crisp Topographic Wireframe Overlay
      const wireframeGeo = new THREE.WireframeGeometry(geometry);
      const wireframeMat = new THREE.LineBasicMaterial({
        color: 0x0A1C14,
        transparent: true,
        opacity: 0.08,
      });
      wireframeMesh = new THREE.LineSegments(wireframeGeo, wireframeMat);
      mesh.add(wireframeMesh);

      // Light Interaction & Mouse Springs
      let mouseX = 0;
      let mouseY = 0;
      let targetRotZ = 0;
      let scrollOffset = 0;

      const handleMouseMove = (e: MouseEvent) => {
        mouseX = (e.clientX / window.innerWidth) * 2 - 1;
        mouseY = -(e.clientY / window.innerHeight) * 2 + 1;
      };

      const handleScroll = () => {
        scrollOffset = window.scrollY * 0.0018;
      };

      window.addEventListener('mousemove', handleMouseMove, { passive: true });
      window.addEventListener('scroll', handleScroll, { passive: true });

      const handleResize = () => {
        if (!renderer || !camera) return;
        const w = window.innerWidth;
        const h = window.innerHeight;
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        renderer.setSize(w, h);
      };
      window.addEventListener('resize', handleResize);

      // Animation Loop
      const clock = new THREE.Clock();

      const animate = () => {
        animId = requestAnimationFrame(animate);
        const time = clock.getElapsedTime() * 0.45;

        targetRotZ += (mouseX * 0.12 - targetRotZ) * 0.05;

        if (mesh && geometry) {
          mesh.rotation.z = targetRotZ;
          mesh.rotation.x = mouseY * 0.08;

          const pos = geometry.attributes.position as THREE.BufferAttribute;
          const col = geometry.attributes.color as THREE.BufferAttribute;
          const posArr = pos.array as Float32Array;
          const colArr = col.array as Float32Array;

          for (let i = 0; i < count; i++) {
            const x = posArr[i * 3];
            const y = posArr[i * 3 + 1];

            // Harmonic biological elevation waves
            const elevation =
              Math.sin(x * 0.35 + time + scrollOffset) * 1.2 +
              Math.cos(y * 0.4 + time * 0.8 + mouseX * 0.5) * 1.1 +
              Math.sin((x + y) * 0.25 - time * 0.6) * 0.6;

            posArr[i * 3 + 2] = elevation;

            // Interpolate color by height (valleys are alabaster, ridges are vivid emerald)
            const normalizedHeight = THREE.MathUtils.clamp((elevation + 2.5) / 5.0, 0, 1);
            const tempCol = new THREE.Color();
            if (normalizedHeight < 0.5) {
              tempCol.lerpColors(colorValley, colorMid, normalizedHeight * 2);
            } else {
              tempCol.lerpColors(colorMid, colorRidge, (normalizedHeight - 0.5) * 2);
            }

            colArr[i * 3] = tempCol.r;
            colArr[i * 3 + 1] = tempCol.g;
            colArr[i * 3 + 2] = tempCol.b;
          }

          pos.needsUpdate = true;
          col.needsUpdate = true;
        }

        if (renderer && scene && camera) {
          renderer.render(scene, camera);
        }
      };

      animate();

      return () => {
        cancelAnimationFrame(animId);
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleResize);

        if (canvas) {
          if (onContextLost) canvas.removeEventListener('webglcontextlost', onContextLost);
          if (onContextRestored) canvas.removeEventListener('webglcontextrestored', onContextRestored);
        }

        if (geometry) geometry.dispose();
        if (wireframeGeo) wireframeGeo.dispose();
        if (material) material.dispose();
        if (wireframeMat) wireframeMat.dispose();
        if (renderer) {
          renderer.dispose();
          if (canvas && canvas.parentNode) {
            canvas.parentNode.removeChild(canvas);
          }
        }
      };
    } catch (err) {
      console.warn('[LivingBiomeCanvas3D failed]:', err);
      setWebglReady(false);
    }
  }, []);

  if (!webglReady) {
    return <CSSBiomePoster />;
  }

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 pointer-events-none z-0 overflow-hidden bg-[#F9FAF7]"
      aria-hidden="true"
    />
  );
};
